import { Injectable } from '@angular/core';
import * as Sentry from "@sentry/angular";
import {ApiRequestService} from "../shared/api-request.service";
import {environment} from "../../environments/environment";

@Injectable({
  providedIn: 'root'
})
export class SentryService {

  constructor(
    private api: ApiRequestService
  ) { }

  /**
   * Set user information and tag with user_id in Sentry.
   *
   * @param {any} value - The user id value to set.
   * @return {void}
   */
  public setUser(value: any) {
    Sentry.setUser({
      id: value
    });
    Sentry.setTag('user_id', value);
  }

  /**
   * Set the active account in Sentry context and tags.
   *
   * @param {any} value - The ID of the active account to set.
   * @return {void}
   */
  public setActiveAccount(value: any) {
    Sentry.setContext('active_account', {
      id: value
    });
    Sentry.setTag('active_account_id', value);
  }

  /**
   * Set the site context and tag in Sentry.
   *
   * @param {any} value - The value to set as the site identifier.
   * @return {void}
   */
  public setSite(value: any) {
    Sentry.setContext('site', {
      id: value
    });
    Sentry.setTag('site_id', value);
  }

  /**
   * Perform a test error handling scenario based on the provided type.
   *
   * @param {string} type - The type of the error scenario to test. Possible values include:
   *   - 'manual': Test manual error captured using Sentry.captureException()
   *   - 'uncaught': Test an uncaught error (should be captured by Angular ErrorHandler)
   *   - 'promise': Test an unhandled promise rejection
   *   - 'http': Test making an HTTP request and handling the response/error
   *   - 'custom': Test throwing a custom error extending Error class
   *   - 'console': Test logging error using console.error
   *   - 'api-runtime', 'api-validation', 'api-query', 'api-queue', 'api-s3', 'api-custom': Test making an API request with specified API type
   *
   * @return {void} - No return value. Throws errors and logs messages based on the provided error type.
   */
  public test(type: string) {
    const apiType = type.replace(/^api-/, ''); // Removes 'api-' prefix if present
    switch (type) {
      case 'manual':
        try {
          throw new Error('Manual error captured using Sentry.captureException()');
        } catch (err) {
          Sentry.captureException(err);
        }
        break;

      case 'uncaught':
        throw new Error('Uncaught error (should be captured by Angular ErrorHandler)');

      case 'promise':
        Promise.reject(new Error('Unhandled promise rejection'));
        break;

      case 'http':
        this.api.makeRequest('get', `v2/unknown`, {}, {})
          .then((response) => {
            console.log(response);
          })
          .catch((_errorResponse) => {
            Sentry.captureException(_errorResponse);
          });
        break;

      case 'custom':
        class CustomSentryError extends Error {
          constructor() {
            super('CustomSentryError triggered');
            this.name = 'CustomSentryError';
          }
        }
        throw new CustomSentryError();

      case 'console':
        console.error('Console.error only: not captured unless redirected');
        break;

      case 'api-runtime':
      case 'api-validation':
      case 'api-query':
      case 'api-queue':
      case 'api-s3':
      case 'api-custom':
        this.api.makeRequest('get', `v2/sentry/throw-error/${apiType}`, {}, {})
          .then((response) => {
            console.log(response);
          })
          .catch((_errorResponse) => {
            console.error(_errorResponse);
          });
        break;

      default:
        console.warn(`Unknown error type: ${type}`);
    }
  }

  /**
   * Method to check if initialization is allowed by making a request to the server.
   *
   * @return {Promise<any>} A Promise indicating if initialization is allowed.
   */
  public allowInit() {
    return this.api.makeRequest('get', `v2/sentry/is-allowed`)
      .then((response: any) => {
        return response;
      })
      .catch((errorResponse) => {
        console.error(errorResponse?.error?.message || 'Failed to check Sentry status.');
        return false; // fallback to false on error
      });
  }

  /**
   * Initializes the Sentry SDK with the provided configuration options.
   *
   * @return {void}
   */
  public onInit() {
    Sentry.init({
      dsn: environment.SENTRY_DSN,
      environment: environment.SENTRY_ENVIRONMENT, // or 'dev', 'staging', 'production', etc.
      release: environment.SENTRY_RELEASE, // Set your release version, such as "getsentry@1.0.0"
      // dist: '<dist>', // optional build/dist identifier
      // Enable logs to be sent to Sentry
      // _experiments: {
      //   enableLogs: true,
      //   beforeSendLog: (log) => {
      //     const logLevel = log.level || 'error';
      //     const allowedLogLevels = ['error', 'fatal']; // Only send these and ignore other levels ('trace', 'debug', 'info', 'warn')
      //     if (!allowedLogLevels.includes(logLevel)) {
      //       // Filter out all other logs
      //       return null;
      //     }
      //     return log;
      //   }
      // },
      integrations: [
        // Registers and configures the Tracing integration,
        // which automatically instruments your application to monitor its
        // performance, including custom Angular routing instrumentation
        Sentry.browserTracingIntegration(),
        // Registers the Replay integration,
        // which automatically captures Session Replays
        Sentry.replayIntegration()
        // send console.error, and console.warn calls as logs to Sentry if supported
        // Sentry.consoleLoggingIntegration({ levels: ["error", "warn"] })
      ],
      // Set tracesSampleRate to 0.1 to capture 10% of transactions for tracing.
      // Learn more at https://docs.sentry.io/platforms/javascript/configuration/options/#traces-sample-rate
      tracesSampleRate: environment.SENTRY_TRACES_SAMPLE_RATE,
      // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
      tracePropagationTargets: [
        /^\/.*/,
        environment.API_URL
      ],
      // Capture Replay for 10% of all sessions, plus for 100% of sessions with an error
      // Learn more at https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
      replaysSessionSampleRate: environment.SENTRY_REPLAYS_SESSION_SAMPLE_RATE,
      replaysOnErrorSampleRate: environment.SENTRY_ON_ERROR_SAMPLE_RATE,
      beforeSend(event, hint) {
        const eventLevel = event.level || 'error';
        const allowedEventLevels = ['error', 'fatal']; // Only send these and ignore other levels ('warning', 'log', 'info', 'debug')
        if (!allowedEventLevels.includes(eventLevel)) {
          return null; // Drop this event
        }

        const error = hint?.originalException;
        // List of known error messages to ignore.
        // When updating, also make changes to partialErrorsToIgnore in app/sentry-error-handler.ts:27
        const ignoredMessages = [
          'ExpressionChangedAfterItHasBeenCheckedError'
        ];
        if (error instanceof Error && ignoredMessages.some(msg => error.message?.includes(msg))) {
          return null; // Ignore this error
        }

        return event;
      }
    });
  }

}
